import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Menu, X } from "lucide-react";

const LINKS = [
  { id: "services", label: "Services", testid: "nav-services-link" },
  { id: "who-we-help", label: "Who We Help", testid: "nav-who-link" },
  { id: "how-it-works", label: "How It Works", testid: "nav-how-link" },
  { id: "founder", label: "Founder", testid: "nav-team-link" },
  { id: "faq", label: "FAQ", testid: "nav-faq-link" },
];

export const LogoMark = () => (
  <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-white p-1 ring-1 ring-pine-900/10">
    <img src="/assets/bee.jpg" alt="SpellingBee Therapy Services logo" className="h-full w-full rounded-full object-contain" />
  </span>
);

export const Navbar = ({ onBook, onNavigate }) => {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const go = (id) => {
    setMenuOpen(false);
    onNavigate(id);
  };

  return (
    <header
      data-testid="navbar"
      className={`fixed inset-x-0 top-0 z-50 transition-[background-color,box-shadow,border-color] duration-500 ${
        scrolled
          ? "bg-cream/85 backdrop-blur-xl border-b border-pine-900/10 shadow-[0_8px_30px_rgba(30,58,110,0.08)]"
          : "bg-transparent"
      }`}
    >
      <nav className="mx-auto flex max-w-7xl items-center justify-between px-5 py-4 sm:px-8">
        <button
          data-testid="navbar-brand"
          onClick={() => go("top")}
          className="flex items-center gap-2.5"
        >
          <LogoMark />
          <span className="leading-none text-left">
            <span className="block font-serif text-lg font-semibold tracking-tight">
              SpellingBee
            </span>
            <span className="mt-0.5 block font-mono text-[9px] uppercase tracking-[0.22em] text-pine-700/70">
              Therapy Services
            </span>
          </span>
        </button>

        <div className="hidden items-center gap-7 lg:flex">
          {LINKS.map((l) => (
            <button
              key={l.id}
              data-testid={l.testid}
              onClick={() => go(l.id)}
              className="group relative text-sm font-medium text-pine-900/70 transition-colors duration-300 hover:text-pine-900"
            >
              {l.label}
              <span className="absolute -bottom-1 left-0 h-0.5 w-0 rounded-full bg-honey transition-[width] duration-300 group-hover:w-full" />
            </button>
          ))}
          <Link
            to="/schools"
            data-testid="nav-schools-link"
            className="group relative text-sm font-semibold text-clay transition-colors duration-300 hover:text-pine-900"
          >
            For Schools
            <span className="absolute -bottom-1 left-0 h-0.5 w-0 rounded-full bg-clay transition-[width] duration-300 group-hover:w-full" />
          </Link>
        </div>

        <div className="hidden items-center gap-3 lg:flex">
          <Link
            to="/admin"
            data-testid="nav-admin-login-button"
            className="text-xs font-mono uppercase tracking-[0.18em] text-pine-900/50 transition-colors hover:text-pine-900"
          >
            Admin
          </Link>
          <button
            data-testid="nav-book-button"
            onClick={onBook}
            className="rounded-full bg-pine-900 px-6 py-2.5 text-sm font-semibold text-cream shadow-md transition-[transform,background-color,box-shadow] duration-300 hover:-translate-y-0.5 hover:bg-pine-800 hover:shadow-lg"
          >
            Book a free consult
          </button>
        </div>

        <button
          data-testid="nav-mobile-menu-button"
          className="lg:hidden"
          onClick={() => setMenuOpen((v) => !v)}
          aria-label="Toggle menu"
        >
          {menuOpen ? <X size={22} /> : <Menu size={22} />}
        </button>
      </nav>

      {menuOpen && (
        <div className="border-t border-pine-900/10 bg-cream/95 px-5 pb-6 pt-3 backdrop-blur-xl lg:hidden" data-testid="nav-mobile-menu">
          {LINKS.map((l) => (
            <button
              key={l.id}
              onClick={() => go(l.id)}
              className="block w-full py-3 text-left font-serif text-2xl"
            >
              {l.label}
            </button>
          ))}
          <Link
            to="/schools"
            data-testid="nav-schools-link-mobile"
            onClick={() => setMenuOpen(false)}
            className="block w-full py-3 text-left font-serif text-2xl text-clay"
          >
            For Schools
          </Link>
          <button
            onClick={() => { setMenuOpen(false); onBook(); }}
            className="mt-3 w-full rounded-full bg-pine-900 py-3 text-sm font-semibold text-cream"
          >
            Book a free consult
          </button>
        </div>
      )}
    </header>
  );
};
